import { useMemo } from "react";
import { createColumnHelper } from "@tanstack/react-table";
import { useTranslation } from "react-i18next";
import moment from "moment-timezone";
import { useGroups } from "../../../Settings/General/OrganisationLevelGroups/context";
import { useCustomFields } from "../../../Settings/CustomFields/context";
import { useCompanyCurrency, useCompanyDateFormat, useCompanyTimeFormat } from "../../../../utils/hooks/company";
import { useAccess } from "../../../../utils/hooks/access";
import { renderDurationAsFormat } from "../../../../utils/helpers/date";
import { renderOrgLevelResourceLabel, renderSettingsResourceLabel } from "../../../../utils/helpers/settings";
import { currencyFormatter } from "../../../../utils/helpers/currencyFormatter";
// import { getTimeSheetDuration } from "../../components/TimeSheetTable/useColumns/useDurationColumn";
// import { getTimeSheetTotal } from "../../components/TimeSheetTable/useColumns/useTotalColumn";

const columnHelper = createColumnHelper();

export const useColumns = () => {
    const { t } = useTranslation();
    // const dateFormat = useCompanyDateFormat();
    const timeFormat = useCompanyTimeFormat();
    const currency = useCompanyCurrency();
    const { groups } = useGroups();
    const { timesheetCustomFields } = useCustomFields();
    const { hasAccess: canViewRates } = useAccess("canViewRates");

    return useMemo(() => {
        const columns = [
            // columnHelper.accessor("date", {
            //     header: t("date"),
            //     meta: { rowSpanBy: "date" },
            //     cell: (info) => {
            //         const value = info.getValue();
            //         return value && moment(value).format(dateFormat);
            //     }
            // }),
            columnHelper.accessor("startDate", {
                header: t("in"),
                cell: ({ row: { original } }) => {
                    const value = original?.startDate;
                    return value && !['amount', 'unit'].includes(original?.payCode?.hourType) ? (
                        <span
                            className="px-3 py-1 rounded"
                            style={{
                                border: "1px solid #00998b",
                                color: "#00998b"
                            }}
                        >
                            {moment.parseZone(value).format(timeFormat)}
                        </span>
                    ) : "-";
                }
            }),
            columnHelper.accessor("endDate", {
                header: t("out"),
                cell: ({ row: { original } }) => {
                    const value = original?.endDate;
                    return value && !['amount', 'unit'].includes(original?.payCode?.hourType) ? (
                        <span
                            className="px-3 py-1 rounded"
                            style={{
                                border: "1px solid #ff6161",
                                color: "#ff6161"
                            }}
                        >
                            {moment.parseZone(value).format(timeFormat)}
                        </span>
                    ) : "-";
                }
            }),
            // columnHelper.accessor("overnight", {
            //     header: t("on"),
            //     cell: ({ row: { original } }) => {
            //         const { startDate, endDate } = original;
            //         let duration = 0;
            //         if (startDate && endDate) {
            //             const parsedStartDate = moment.parseZone(startDate);
            //             const parsedEndDate = moment.parseZone(endDate);
            //             if (parsedEndDate.date() !== parsedStartDate.date()) {
            //                 duration = parsedEndDate.date() - parsedStartDate.date();
            //             }
            //         }
            //         return duration > 0 ? `+${duration}` : "--";
            //     }
            // }),
            columnHelper.accessor("payCode", {
                header: t("time-code"),
                cell: (info) => {
                    const value = info.getValue();
                    return value ? renderSettingsResourceLabel(value) : null;
                }
            }),
            // columnHelper.accessor("payCode.description", {
            //     header: t("time-code-description")
            // }),
            columnHelper.accessor("payCode.hourType", {
                header: t("hour-type"),
                cell: (info) => {
                    const value = info.getValue();
                    return t(value);
                }
            }),
            columnHelper.accessor("duration", {
                header: t("duration"),
                cell: ({ row: { original } }) => {
                    const value = original?.duration;
                    return value && !['amount'].includes(original?.hourType) ? renderDurationAsFormat(value || 0, "HH:mm", true, true) : "-";
                }
            }),
            columnHelper.accessor("otherDuration", {
                header: t("premium"),
                cell: ({ row: { original } }) => {
                    const value = original?.otherDuration;
                    return value && !['amount'].includes(original?.hourType) ? renderDurationAsFormat(value, "HH:mm", true, true) : "-";
                }
            }),
            // columnHelper.display({
            //     id: "date-hours",
            //     header: t("diff-hours"),
            //     meta: { rowSpanBy: "date" },
            //     cell: ({ row: { original }, table }) => {
            //         const data = table.options.data;
            //         const duration = data
            //             .filter((timeSheet) => {
            //                 if (timeSheet.hourType === "unpaid") return false;
            //                 if (timeSheet.payCode.excludeFromPaidHours) return false;
            //                 return moment(timeSheet.date).isSame(moment(original.date), "date");
            //             })
            //             .reduce(
            //                 (total, timeSheet) =>
            //                     total + timeSheet.duration,
            //                 0
            //             );

            //         return (
            //             <div className={`font-weight-bold text-center ${duration < 0 ? "text-danger" : duration > 0 ? "text-success" : "text-muted"}`}>
            //                 {renderDurationAsFormat(duration, timeFormat, true, true)}
            //             </div>
            //         );
            //     },
            // }),
            columnHelper.accessor("location", {
                header: t("location"),
                cell: (info) => {
                    const value = info.getValue();
                    return value ? renderSettingsResourceLabel(value) : null;
                }
            }),
            columnHelper.accessor("job", {
                header: t("job"),
                cell: (info) => {
                    const value = info.getValue();
                    return value ? renderSettingsResourceLabel(value) : null;
                }
            })
        ];

        if (groups?.length > 0) {
            const groupColumns = groups.map((group) =>
                columnHelper.accessor(group.level.replaceAll("_", ""), {
                    header: group.description,
                    cell: ({ row: { original } }) => {
                        const value = original?.[group.level.replaceAll("_", "")];
                        return value ? renderOrgLevelResourceLabel(value) : null;
                    }
                }));
            columns.push(...groupColumns);
        }

        if (timesheetCustomFields?.length > 0) {
            const customFieldsColumns = timesheetCustomFields.map((item) =>
                columnHelper.accessor(`customFields.${item.code}`, {
                    header: item.description,
                    cell: (info) => {
                        const value = info.getValue();
                        return value ? renderOrgLevelResourceLabel(value) : null;
                    }
                }));
            columns.push(...customFieldsColumns);
        }

        if (canViewRates) {
            columns.splice(4, 0, columnHelper.accessor("rate", {
                header: t("rate"),
                cell: ({ row: { original } }) => {
                    return currencyFormatter(original.rate, 2, original.job.currency || currency);
                }
            }));

            columns.splice(7, 0, columnHelper.accessor("total", {
                header: t("total"),
                cell: ({ row: { original } }) => {
                    return currencyFormatter(original?.total || 0, 2, original?.job?.currency || currency);
                }
            }));

            // columns.splice(9, 0, columnHelper.display({
            //     id: "date-total",
            //     header: t("diff-amount"),
            //     meta: { rowSpanBy: "date" },
            //     cell: ({ row: { original }, table }) => {
            //         const data = table.options.data;
            //         const total = data
            //             .filter((timeSheet) => {
            //                 return moment(timeSheet.date).isSame(moment(original.date), "date") ||
            //                     !!timeSheet?.isNonProductiveTime;
            //             })
            //             .reduce(
            //                 (total, timeSheet) =>
            //                     total + timeSheet.total,
            //                 0
            //             );

            //         return (
            //             <div className={`font-weight-bold text-center ${total < 0 ? "text-danger" : total > 0 ? "text-success" : "text-muted"}`}>
            //                 {currencyFormatter(total || 0, 2, original.job.currency || currency)}
            //             </div>
            //         );
            //     },
            // }))
        }

        return columns;
    }, [canViewRates, currency, groups, timesheetCustomFields, timeFormat, t]);
}